import Button from '~/common/components/Button';
import CrudGrid from '~/common/components/CrudGrid';
import { OPERATORS } from '~/common/components/CrudGrid/Filters/filterGridData';
import type { GridDef } from '~/common/components/CrudGrid/GridDef';
import { renderBoolean } from '~/common/components/CrudGrid/crudGridUtils';
import Flex from '~/common/components/Flex';
import Text from '~/common/components/Texts/Text';
import useIsWriteAccessDisabled from '~/common/hooks/useIsWriteAccessDisabled';
import useUrl from '~/common/hooks/useUrl';
import { formatCurrency } from '~/common/util/format';
import { formatName } from '~/pages/Loan1003DataGrids/common/utils/utils';

import { use1003LiabilitiesData, useDeleteLiabilitiesMutation } from '../hooks';
import { useLiability1003Store } from '../liability1003Store';
import { LiabilityDeleteMessageDialog } from './LiabilityDeleteMessageDialog';

const columns = [
  {
    fieldName: 'creditorName',
    headerName: 'Creditor',
  },
  {
    fieldName: 'liabilitySummaryType',
    headerName: 'Type',
    useOptions: true,
    renderCell: (val: string, options) => {
      if (val === null || val === '') return '-';
      return (
        options?.availableLiabilityTypes?.find(
          (value) => value.value.toString() === val.toString(),
        )?.label ?? '-'
      );
    },
  },
  {
    fieldName: 'accountNumber',
    headerName: 'Account Number',
  },
  {
    fieldName: 'payment',
    headerName: 'Payment',
    isNumeric: true,
    renderCell: (val: number | string) => formatCurrency(val),
  },
  {
    fieldName: 'excludePayment',
    headerName: 'Exclude',
    renderCell: (val: boolean) => renderBoolean(val),
  },
  {
    fieldName: 'exclusionReason',
    headerName: 'Exclusion Reason',
    useOptions: true,
    renderCell: (val: string | number, options) => {
      if (val === null || val === '') return '-';
      return (
        options?.availableExclusionReasons?.find(
          (reason) => reason.value.toString() === val.toString(),
        )?.label ?? '-'
      );
    },
  },
  {
    fieldName: 'payoffRequired',
    headerName: 'Payoff',
    renderCell: (val: boolean) => renderBoolean(val),
  },
  {
    fieldName: 'balance',
    headerName: 'Balance',
    isNumeric: true,
    renderCell: (val: number | string) => formatCurrency(val),
  },
];

const multiDelete = {
  buttonLabel: 'Delete Multiple Liabilities',
  subActions: [
    {
      label: 'Select All / Deselect All',
      id: 'selectAll',
      action: (allData, currentSelected) => {
        if (currentSelected.length === allData.length) {
          return [];
        }
        return allData;
      },
    },
    {
      label: 'Select All Zero Balance',
      id: 'selectAllZeroBalance',
      action: (allData) => {
        return allData.filter((data) => !data.balance);
      },
    },
  ],
};

export function LiabilitiesCrudGrid() {
  const { data, isLoading } = use1003LiabilitiesData();
  const { isReadOnly } = useUrl();
  const { mutate: deleteLiabilities } = useDeleteLiabilitiesMutation();

  const isWriteAccessDisabled =
    useIsWriteAccessDisabled() || isReadOnly || !data?.hasWritePermission;

  const { selectedLiabilityId, setSelectedLiabilityId } =
    useLiability1003Store();

  const gridDef: GridDef = {
    defaultSort: { field: 'balance', direction: 'desc' as const },
    uniqueIdAttributeFields: ['liabilityIdentifier.debtId'],
    allowFilters: true,
    gridLanguage: {
      singularItem: 'Liability',
      pluralItem: 'Liabilities',
    },
    getDialogDeleteMessage: (rows) => LiabilityDeleteMessageDialog({ rows }),
    filters: [
      {
        options: data?.availableBorrowers?.map((borrower) => {
          return {
            value: borrower.customerRecordId,
            label: `${borrower.firstName} ${borrower.lastName}`,
          };
        }),
        label: 'Borrower(s)',
        fieldName: 'customerRecordId',
      },
    ],
  };

  const filters = [
    {
      options: data?.availableBorrowers?.map((borrower) => {
        return {
          value: borrower.customerRecordId,
          label: formatName(
            borrower.firstName,
            borrower.lastName,
            borrower.middleName,
            borrower.suffixName,
          ),
        };
      }),
      label: 'Borrower(s)',
      fieldName: 'customerIdentifiers',
      operator: OPERATORS.EXCLUDE,
      evaluateFilter: (row, value) =>
        row.customerIdentifiers.some((borrower) =>
          value.includes(borrower.borrowerRecordId),
        ),
    },
  ];

  return (
    <CrudGrid
      data={data?.liabilities ?? []}
      columns={columns}
      filters={filters}
      isLoading={isLoading}
      gridDef={gridDef}
      onRowEditClick={(row) => {
        setSelectedLiabilityId(row.liabilityIdentifier.debtId);
      }}
      selectedRow={
        selectedLiabilityId
          ? { liabilityIdentifier: { debtId: selectedLiabilityId } }
          : null
      }
      onGridModeChange={() => {
        setSelectedLiabilityId(null);
      }}
      multiSelectActionDef={multiDelete}
      dropdownOptions={data?.dropdownOptions ?? []}
      onDelete={(rows) => {
        deleteLiabilities(rows);
      }}
      readOnly={isWriteAccessDisabled}
      actions={
        <Button
          dataTestid="addLiabilityButton"
          onClick={() => {
            useLiability1003Store.setState({
              selectedLiabilityId: 0,
            });
          }}
        >
          Add Liability
        </Button>
      }
      summary={
        <Flex>
          <Text fontWeight={'bold'} padding={'0rem 1rem'}>
            Total Payment:{' '}
            {formatCurrency(data?.totalBalanceAndPayment.payments ?? 0)}
          </Text>
          <Text fontWeight={'bold'}>
            Total Balance:{' '}
            {formatCurrency(data?.totalBalanceAndPayment.balance ?? 0)}
          </Text>
        </Flex>
      }
    />
  );
}
